import type {PageProps} from 'gatsby'
import {graphql} from 'gatsby'
import React from 'react'

import Footer from '@/components/footer'
import Header from '@/components/header'
import type {Props as HeaderProps} from '@/components/header'
import SEO from '@/components/seo'
import type {SlicePageQuery} from '@/graphql-types'
import {SliceZone} from '@/slices/slice-zone'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {withPrismicPreview} from '@/utils/with-prismic-preview'
import {Main} from '@helios/shared/components/skip-to-content'
import {useLocale} from '@helios/shared/providers/locale'
import type {GatsbyHeadProps} from '@helios/shared/types/gatsby-head'

type Props = PageProps<SlicePageQuery>

const SlicePage = (props: Props) => {
    const data = props.data.prismicSlicePage.data
    const currentLocale = useLocale()

    if (!data) {
        return handleNoPrismicData()
    }

    return (
        <>
            <Header
                hideLeftNav={currentLocale.isPreLaunch || data.hide_left_nav}
                hideRightNav={currentLocale.isPreLaunch || data.hide_right_nav}
                ctaDestination={
                    (data.header_cta_destination as HeaderProps['ctaDestination']) ?? undefined
                }
            />

            <Main>
                <SliceZone slices={data.body} />
            </Main>

            <Footer hideLinks={data.hide_links} />
        </>
    )
}

export const Head = ({location, pageContext, data}: GatsbyHeadProps<SlicePageQuery>) => {
    const pageData = data.prismicSlicePage.data

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={pageData.meta_title}
            description={pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image?.url}
            preventIndexing={pageData.exclude_robots}
        />
    )
}

export const query = graphql`
    query SlicePage(
        $uid: String!
        $locale: String!
        $imageBreakpoints: [Int]
        $twoColumns: String
        $twoColumnsAsymmetric: String
        $threeColumns: String
        $fourColumns: String
    ) {
        prismicSlicePage(uid: {eq: $uid}, lang: {eq: $locale}) {
            _previewable
            uid
            data {
                hide_left_nav
                hide_right_nav
                hide_links
                header_cta_destination
                meta_title
                meta_description
                exclude_robots
                open_graph_image {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                body {
                    ...CardGridSlice
                    ...ComponentPlaceholderSlice
                    ...ContentPlusBlocksSlice
                    ...ContentPlusShortBulletsSlice
                    ...CustomersLogosPlusHeadlineSlice
                    ...CustomersSlice
                    ...FAQSlice
                    ...FeatureBlockSlice
                    ...FeatureGridSlice
                    ...GetStartedSlice
                    ...HeroSlice
                    ...HubspotFormSlice
                    ...LandingHeroSlice
                    ...MediaItemGridSlice
                    ...PricingSlice
                    ...RatingsPlusHeadlineSlice
                    ...SocialProofSlice
                    ...StatisticsSlice
                    ...VideoSlice
                    ...Countdown
                    ...Highlight
                    ...ComparisonTable
                    ...SliderCardsSlice
                    ...TwoImageHero
                    ...Steps
                    ...GlassdoorWidgetSlice
                    ...JobsGridSlice
                    ...LatestBlogPostsSlice
                    ...VideoPopoverWithTextSlice
                    ...ContactHeroSlice
                    ...ContactEmailsSlice
                    ...ContactFormSlice
                    ...ChatSlice
                    ...SlicePageIntegrations
                    ...IntegrationCards
                    ...SimpleCardGridSlice
                    ...SlicePageCustomerLogosPlusCta
                    ...EventCardsSlice
                    ...VideoLibrarySlice
                    ...PartnerProgrammeSlice
                    ...InteractiveDemoSlice
                    ...RichTextSlice
                    ...UpcomingEventsSlice
                    ...OnDemandResourcesSlice
                }
            }
        }
    }

    fragment SlicePageIntegrations on PrismicSlicePageDataBodyIntegrations {
        __typename
        primary {
            title
            cta_text
            cta_link {
                ...PrismicLink
            }
            more_text
            logo_1 {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            logo_1_link {
                ...PrismicLink
            }
            logo_2 {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            logo_2_link {
                ...PrismicLink
            }
            logo_3 {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            logo_3_link {
                ...PrismicLink
            }
        }
    }

    fragment SlicePageCustomerLogosPlusCta on PrismicSlicePageDataBodyCustomerLogosPlusCta {
        __typename
        primary {
            title
            cta_text
            cta_link {
                ...PrismicLink
            }
            marquee_mode
            background_color
        }
        items {
            logo {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
        }
    }
`

export default withPrismicPreview(SlicePage)
